<script setup lang="ts">
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'

  import useCommonMixin from '@/composables/useCommonMixin'
  import useIcons from '@/composables/useIcons'
  import useCourse from '@/composables/useCourse'
  import useShopifyClient from '@/composables/useShopifyClient'

  const { hasValidUser } = useCommonMixin()
  const { course, isTrialExpired, lessonsAndDuration, coursePurchasePerksToShow } = useCourse()
  const { createShopifyCheckout } = useShopifyClient()

  import { UserModule } from '@/store/modules/user'

  const { mediaIcon, starIcon, arrowRightIndicator } = useIcons()

  const buyNow = () => {
    createShopifyCheckout(UserModule.email || undefined, course.value?.shopifyProductVariantId)
  }
</script>

<template lang="pug">
.buy-course-details(
  :class='{ "trial-expired-and-not-purchased": hasValidUser && isTrialExpired(course) && !course?.purchased }'
)
  .perks-wrapper
    .course-perks-overview
      .buy-course-reviews
        h2.ketch-font-bold {{ $t('courses.buyCourse') }}
        .course-rating(v-if='course?.rating')
          SVGRenderer(
            :icon='starIcon',
            :stroke-color='"var(--primary-color)"',
            :fill-color='"var(--primary-color)"',
            :has-hover='false',
            width='20px',
            height='20px'
          )
          h5.ketch-font-bold {{ course?.rating }}
          h5 ({{ course?.totalCourseRatings }})
      .course-content-summary
        SVGRenderer(
          :icon='mediaIcon',
          :stroke-color='"var(--primary-text-color)"',
          :has-hover='false',
          width='30px',
          height='30px'
        )
        h5
          span.ketch-font-bold {{ lessonsAndDuration }}
          span {{ $t('contentPreparedByExperts') }}
      .content-separator.ketch-w-c100
    .purchasing-perks
      .perk-item(v-for='perk in coursePurchasePerksToShow', :key='perk.titleKey')
        SVGRenderer(
          :icon='perk.icon',
          :stroke-color='"var(--primary-text-color)"',
          :has-hover='false',
          width='30px',
          height='30px'
        )
        .perk-content
          h5.ketch-font-bold {{ $t(perk.titleKey, { successRate: course?.successRate }) }}
          h5.content-description {{ $t(perk.descriptionKey, { totalExamsPassed: course?.totalExamsPassed }) }}
  .course-price-cta
    h1.course-price Ab {{ course?.shopifyProductPrice }}€
    KetchUpButton.primary(@click.native='buyNow')
      h4.ketch-font-bold {{ $t('selectCourse') }}
      SVGRenderer(
        :icon='arrowRightIndicator',
        :fill-color='"var(--primary-foreground-color)"',
        :has-hover='false',
        width='15px',
        height='12px'
      )
</template>

<style lang="postcss" scoped>
  .buy-course-details {
    @apply ketch-flex ketch-flex-col ketch-space-y-c15 ketch-bg-content-background-color ketch-p-c10;
    @apply ketch-border-[2px] ketch-border-primary-color ketch-rounded-normal;
    @apply xs1:ketch-p-c15 sm:ketch-bg-transparent;

    &.trial-expired-and-not-purchased {
      @apply ketch-border-[#FF3459];
    }

    .course-perks-overview {
      @apply ketch-flex ketch-flex-col ketch-space-y-c10;

      .buy-course-reviews {
        @apply ketch-flex ketch-items-center ketch-justify-between;

        .course-rating {
          @apply ketch-flex ketch-space-x-c5;
        }
      }
    }

    .purchasing-perks {
      @apply ketch-flex ketch-flex-col ketch-space-y-c10 ketch-mt-c15;

      .perk-item {
        @apply ketch-flex ketch-items-center ketch-space-x-c10;

        .perk-content {
          @apply ketch-flex ketch-flex-col;
        }
      }
    }

    .course-price-cta {
      @apply ketch-flex ketch-items-center ketch-justify-between ketch-border-t ketch-border-border-color;
      @apply ketch-pt-c10 ketch-px-c10 ketch--mx-c10 xs1:ketch-pt-c15 xs1:ketch-px-c15 xs1:ketch--mx-c15;

      .course-price {
        @apply ketch-font-bold;
      }

      button {
        @apply ketch-w-auto ketch-space-x-c10 ketch-px-c10 sm:ketch-px-c20 sm:ketch-w-[180px] lg:ketch-w-[200px];
        filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.15));
      }
    }
  }
</style>
